import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Home, Building2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const Education = () => {
  // Analytics tracking for partner link clicks
  const trackPartnerClick = (partner, url) => {
    // Log to console for debugging
    console.log(`[Analytics] Partner click: ${partner}`, { url, timestamp: new Date().toISOString() });
    
    // Store click in localStorage for simple analytics
    const clicks = JSON.parse(localStorage.getItem('education_partner_clicks') || '[]');
    clicks.push({
      partner,
      url,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('education_partner_clicks', JSON.stringify(clicks));
    
    // Show toast confirmation
    toast.success(`Opening ${partner} education portal...`);
  };

  return (
    <div className="min-h-screen">
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#BCA480] to-[#a89470] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Get Educated</h1>
          <p className="text-xl max-w-3xl mx-auto text-white/90">
            Thinking about a career in real estate or mortgage lending? Start your journey with 
            trusted education partners and take the first step toward licensure.
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#434345] mb-4">Not Sure Where to Start?</h2>
            <p className="text-lg text-gray-600">
              If you're considering real estate or mortgage lending and don't know where to begin, 
              watch this short video.
            </p>
          </div>
          
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <video 
              controls 
              className="w-full"
              poster="/api/placeholder/800/450"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/4wzqcwsl_Intro%20to%20Education%282%29.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Education Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#434345] mb-12">
            Choose Your Path
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Real Estate Education Card */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-[#BCA480]/10 rounded-lg">
                    <Home className="h-8 w-8 text-[#BCA480]" />
                  </div>
                  <CardTitle className="text-2xl">Real Estate Licensing & Continuing Education</CardTitle>
                </div>
                <CardDescription className="text-base">
                  Start or advance your real estate career with flexible, self-paced education through 
                  Colibri Real Estate.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  asChild
                  className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white mb-4"
                  size="lg"
                  data-testid="real-estate-education-btn"
                >
                  <a 
                    href="https://colibri-real-estate.pxf.io/BespokeRealty" 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackPartnerClick('Colibri Real Estate', 'https://colibri-real-estate.pxf.io/BespokeRealty')}
                  >
                    Start Real Estate Education
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </Button>
                <p className="text-sm text-gray-500 text-center">
                  Ideal for aspiring real estate agents and licensed professionals.
                </p>
              </CardContent>
            </Card>

            {/* Mortgage Education Card */}
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-[#BCA480]/10 rounded-lg">
                    <Building2 className="h-8 w-8 text-[#BCA480]" />
                  </div>
                  <CardTitle className="text-2xl">Mortgage Licensing & Education</CardTitle>
                </div>
                <CardDescription className="text-base">
                  Explore mortgage education and licensing programs designed for future and current 
                  mortgage professionals.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  asChild
                  className="w-full bg-[#BCA480] hover:bg-[#a89470] text-white mb-4"
                  size="lg"
                  data-testid="mortgage-education-btn"
                >
                  <a 
                    href="https://partners.oncourselearning.com/mortgage/bespoke" 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackPartnerClick('OnCourse Learning', 'https://partners.oncourselearning.com/mortgage/bespoke')}
                  >
                    Start Mortgage Education
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </Button>
                <p className="text-sm text-gray-500 text-center">
                  Powered by Colibri / OnCourse Learning.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#BCA480]/10 to-white p-8 rounded-lg border border-[#BCA480]/20">
            <p className="text-lg text-gray-700 mb-6">
              Already licensed? Once you complete your education, we hope you'll consider 
              <strong className="text-[#BCA480]"> Bespoke Realty</strong> as the place to grow your career.
            </p>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-[#BCA480] text-[#BCA480] hover:bg-[#BCA480] hover:text-white"
            >
              <Link to="/apply">
                Join Our Team
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Education;
